import axios from "axios";
import { Check, Gift, Loader2, Lock, Pencil } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

/** Estado de "quem indicou esta loja" (GET /api/referral/minha-indicacao). */
export function useIndicacao() {
  const [estado, setEstado] = useState(null);
  const recarregar = useCallback(() => axios.get("/api/referral/minha-indicacao", auth())
    .then((r) => setEstado(r.data)).catch(() => setEstado(null)), []);
  useEffect(() => { recarregar(); }, [recarregar]);
  return { estado, setEstado, recarregar, precisaPerguntar: !!(estado?.pode_editar && !estado?.respondeu) };
}

/** O formulário: "Sim, tenho o código" (com o nome de quem indicou ao digitar) ou "Não tive indicação". */
function Formulario({ estado, onSalvo, textoBotao = "Salvar" }) {
  const [opcao, setOpcao] = useState(estado?.resposta === "nao" ? "nao" : "sim");
  const [codigo, setCodigo] = useState(estado?.indicador?.codigo || "");
  const [quem, setQuem] = useState(estado?.indicador?.nome || null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    const c = codigo.trim();
    if (opcao !== "sim" || c.length < 4) { setQuem(null); return undefined; }
    const t = setTimeout(() => {
      axios.get(`/api/referral/validate/${encodeURIComponent(c)}`).then((r) => setQuem(r.data?.valid ? r.data.referrer_name : false)).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [codigo, opcao]);

  const salvar = async () => {
    setSalvando(true);
    try {
      const r = await axios.put("/api/referral/minha-indicacao", opcao === "nao" ? { nao_teve: true } : { codigo: codigo.trim() }, auth());
      toast.success(opcao === "nao" ? "Anotado: sem indicação" : `Indicação registrada: ${r.data?.indicador?.nome || "ok"}`);
      onSalvo?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar");
    } finally {
      setSalvando(false);
    }
  };

  const pronto = opcao === "nao" || (codigo.trim() && quem);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {[["sim", "Sim, tenho o código"], ["nao", "Não tive indicação"]].map(([v, r]) => (
          <button key={v} type="button" onClick={() => setOpcao(v)} data-e2e={`indicacao-${v}`}
            className={`h-10 rounded-lg border-2 text-sm font-semibold ${opcao === v ? "border-primary bg-primary/5 text-primary" : "border-transparent bg-muted/60 text-muted-foreground"}`}>
            {r}
          </button>
        ))}
      </div>
      {opcao === "sim" && (
        <div>
          <input value={codigo} onChange={(e) => setCodigo(e.target.value.toUpperCase().replace(/\s/g, ""))} placeholder="Código de quem indicou"
            className="h-10 w-full rounded-lg border bg-background px-3 text-sm font-mono font-bold tracking-wider uppercase outline-none focus:ring-2 focus:ring-primary/30"
            data-e2e="indicacao-codigo" />
          {quem && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Indicação de {quem}</p>}
          {quem === false && <p className="text-xs text-red-500 mt-1.5">Código não encontrado. Confira com quem indicou.</p>}
        </div>
      )}
      <button type="button" onClick={salvar} disabled={!pronto || salvando} data-e2e="indicacao-salvar"
        className="h-10 w-full rounded-lg bg-primary text-primary-foreground text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {salvando && <Loader2 className="w-4 h-4 animate-spin" />} {textoBotao}
      </button>
    </div>
  );
}

/**
 * Assinatura → "Alguém indicou o Núcleo para você?" (pedido do dono, 23/10/2026).
 * Aparece até a primeira fatura ser paga: a loja informa o código de quem indicou
 * (ou que não teve) e pode mudar até pagar. Depois de paga, o vínculo trava e
 * quem indicou ganha os meses grátis sozinho. Só proprietário e administrador mudam.
 */
export default function QuemIndicou({ indicacao, podeAlterar }) {
  const { estado, setEstado } = indicacao;
  const [editando, setEditando] = useState(false);
  if (!estado) return null;
  const { pode_editar: aberta, respondeu, indicador } = estado;

  if (!aberta) {
    if (!indicador) return null;
    return (
      <p className="text-sm text-muted-foreground flex items-center gap-2 px-1" data-e2e="indicacao-travada">
        <Lock className="w-3.5 h-3.5" /> Sua loja veio por indicação de <b className="text-foreground">{indicador.nome}</b>.
      </p>
    );
  }
  if (respondeu && !editando) {
    return (
      <div className="bg-card border rounded-2xl px-5 py-3.5 flex items-center gap-3 flex-wrap" data-e2e="indicacao-respondida">
        <Gift className="w-5 h-5 text-primary shrink-0" />
        <p className="text-sm min-w-0 flex-1">
          {indicador ? <>Indicação de <b>{indicador.nome}</b> registrada.</> : "Você informou que não teve indicação."}
          <span className="block text-xs text-muted-foreground">Dá para mudar até o pagamento da primeira fatura.</span>
        </p>
        {podeAlterar && (
          <button type="button" onClick={() => setEditando(true)} className="h-9 px-3 rounded-lg border text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-muted">
            <Pencil className="w-3.5 h-3.5" /> Mudar
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="rounded-2xl border-2 border-primary/40 bg-primary/5 p-5 space-y-3" data-e2e="indicacao-pergunta">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-primary/15 text-primary grid place-items-center shrink-0"><Gift className="w-5 h-5" /></span>
        <div>
          <p className="font-bold">Alguém indicou o Núcleo para você?</p>
          <p className="text-sm text-muted-foreground">Informe o código de quem indicou: quando sua primeira fatura for paga, essa loja ganha {estado.meses_premio} mensalidades grátis.</p>
        </div>
      </div>
      {podeAlterar
        ? <Formulario estado={estado} onSalvo={(d) => { setEstado(d); setEditando(false); }} />
        : <p className="text-xs text-muted-foreground">Só o proprietário ou um administrador pode informar a indicação.</p>}
    </div>
  );
}

/** A mesma pergunta, ao tocar em pagar a primeira fatura sem ter respondido. */
export function PerguntaAntesDePagar({ aberta, indicacao, onCancelar, onContinuar }) {
  return (
    <Dialog open={aberta} onOpenChange={(v) => !v && onCancelar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Antes de pagar: teve indicação?</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          Se alguém indicou o Núcleo para você, informe o código agora: com o pagamento desta primeira fatura, essa loja
          ganha {indicacao.estado?.meses_premio || 2} mensalidades grátis.
        </p>
        {indicacao.estado && <Formulario estado={indicacao.estado} textoBotao="Salvar e ir para o pagamento"
          onSalvo={(d) => { indicacao.setEstado(d); onContinuar(); }} />}
        {/* Nunca trava o pagamento: se salvar falhar (ou a pessoa não quiser responder agora), segue */}
        <button type="button" onClick={onContinuar}
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 mx-auto block">
          Responder depois e pagar agora
        </button>
      </DialogContent>
    </Dialog>
  );
}
